

import React, {useState} from 'react';

import springboard from 'springboard';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import 'mantine-contextmenu/styles.css';
// import '@mantine/dropzone/styles.css';

import {themes as allThemes, sortedThemeNames} from './themes';

import {Notifications, notifications} from '@mantine/notifications';

import {Group, MantineProvider as Mantine, Select, Text} from '@mantine/core';
import {ContextMenuProvider} from 'mantine-contextmenu';
import {useAction, useSelectorWithRefresh} from '@/store/kysely_module';
import {UserPreferenceType} from '@/store/kysely_types';

declare module 'springboard/module_registry/module_registry' {
    interface AllModules {
        Mantine: MantineModuleReturnValue;
    }
}

type MantineModuleReturnValue = {
    ChangeThemeModalForm: React.ElementType<{close: () => void}>;
    Provider: React.ElementType<React.PropsWithChildren>;
};

springboard.registerModule('Mantine', {}, async (moduleAPI): Promise<MantineModuleReturnValue> => {
    const savedTheme = await moduleAPI.statesAPI.createUserAgentState<keyof typeof allThemes>('customTheme', 'songdrive_dark');

    const ChangeThemeModalForm = ({close}: {close: () => void}) => {
        const setUserPreference = useAction('setUserPreference');
        const [userPreference, refreshThemePreference, isLoading] = useSelectorWithRefresh('getUserPreference', {key: UserPreferenceType.theme}, null, {mode: 'local'});

        const currentTheme = userPreference?.value || 'songdrive_dark';

        return (
            <Group>
                <Select
                    value={currentTheme}
                    onChange={async (value) => {
                        if (value) {
                            try {
                                await setUserPreference({key: UserPreferenceType.theme, value}, {mode: 'local'}); // TODO: may want to revisit this
                                // Update local state for immediate UI update
                                savedTheme.setState(value as keyof typeof allThemes);
                                // Refresh the user preference to trigger updates
                                refreshThemePreference();
                                close();
                            } catch (error) {
                                console.error('Failed to save theme preference:', error);
                            }
                        }
                    }}
                    data={sortedThemeNames.map((theme) => ({
                        value: theme,
                        label: allThemes[theme as keyof typeof allThemes].label,
                    }))}
                    disabled={isLoading}
                />
            </Group>
        );
    };

    const Provider = (props: React.PropsWithChildren) => {
        const [userPreference] = useSelectorWithRefresh('getUserPreference', {key: UserPreferenceType.theme}, null, {workspace_id: null});
        const localTheme = savedTheme.useState();

        // Use UserPreference if available, otherwise fall back to savedTheme state, then default
        const currentThemeKey = userPreference?.value || localTheme || 'songdrive_dark';
        const mantineTheme = allThemes[currentThemeKey as keyof typeof allThemes]?.mantineTheme || allThemes.songdrive_dark.mantineTheme;

        // Sync the local state with the UserPreference when it loads
        React.useEffect(() => {
            if (userPreference?.value && userPreference.value !== localTheme) {
                savedTheme.setState(userPreference.value as keyof typeof allThemes);
            }
        }, [userPreference?.value, localTheme]);

        return (
            <Mantine
                defaultColorScheme='dark'
                forceColorScheme='dark'
                withGlobalClasses
                theme={mantineTheme}
            >
                <Notifications
                    position='top-right'
                    zIndex={10000} // we need to go on top of the mobile bottom sheet
                    portalProps={{
                        // target: document.body,
                    }}
                />
                <ContextMenuProvider>
                    {props.children}
                </ContextMenuProvider>
            </Mantine>
        );
    };

    return {
        ChangeThemeModalForm,
        Provider,
    };
});


